// admin/src/components/Layout.tsx
import {
  Drawer, List, ListItem, ListItemText, Toolbar, AppBar, Typography,
  Box, CssBaseline, Divider, ListItemButton, Avatar, Menu, MenuItem,
  IconButton, Tooltip, Collapse,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { removeToken } from '../utils/auth';
import logo from '../assets/logo.png';

const drawerWidth = 240;

const Layout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const storedUser = localStorage.getItem('user');
  const loggedInUser = storedUser ? JSON.parse(storedUser) : { fullName: 'Unknown User', avatar: '' };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    removeToken();
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(to right, #6226A7, #F6A938)',
          color: '#fff',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap>QuillStack Admin</Typography>

          <Box>
            <Tooltip title="Account settings">
              <IconButton onClick={handleMenuOpen} size="small" sx={{ ml: 2 }}>
                <Avatar
                  alt={loggedInUser.fullName}
                  src={loggedInUser.avatar || ''}
                  sx={{ width: 36, height: 36 }}
                />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{ elevation: 3, sx: { mt: 1.5, minWidth: 180 } }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled>
                <Typography variant="subtitle2" fontWeight={600}>{loggedInUser.fullName}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => navigate('/profile')}>Profile</MenuItem>
              <MenuItem onClick={() => navigate('/profile/password')}>Reset Password</MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#F0F5F4',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <img src={logo} alt="QuillStack Logo" width={120} />
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <List>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/dashboard" selected={location.pathname === '/dashboard'}>
                <ListItemText primary="Dashboard" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton component={Link} to="/notes" selected={location.pathname.startsWith('/notes')}>
                <ListItemText primary="Notes" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton component={Link} to="/tunes" selected={location.pathname.startsWith('/tunes')}>
                <ListItemText primary="Tunes" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton component={Link} to="/password-generation" selected={location.pathname.startsWith('/password-generation')}>
                <ListItemText primary="Password Generation" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton component={Link} to="/internet-speed" selected={location.pathname.startsWith('/internet-speed')}>
                <ListItemText primary="Internet Speed" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton component={Link} to="/users" selected={location.pathname.startsWith('/users')}>
                <ListItemText primary="Users" />
              </ListItemButton>
            </ListItem>

          </List>
        </Box>
      </Drawer>

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // prevent flex child overflow
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
